import { useTranslation } from "react-i18next";
import { useState, useMemo, useRef, useEffect } from "react";
import { Plus } from "lucide-react";
import { useRecommendations } from "@/hooks/use-recommendations";
import { useProductsLookup } from "@/hooks/use-products";
import { ProductThumbnail } from "@/components/products/ProductThumbnail";
import { ProductDetailsDialog } from "@/components/products/ProductDetailsDialog";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { useProductEvents } from "@/hooks/use-product-events";
import type { Product } from "@/types/product";

const MAX_ITEMS = 5;
const MAX_NAME_LENGTH = 40;

interface RecommendationChipsProps {
  cartProductIds: string[];
  onAddProduct: (product: Product, quantity: number) => void;
}

function RecommendationCard({
  product,
  onAdd,
  onViewDetails,
}: {
  product: Product;
  onAdd: () => void;
  onViewDetails: () => void;
}) {
  const { t } = useTranslation();
  const name = product.description ?? product.uid;
  const truncatedName =
    name.length > MAX_NAME_LENGTH
      ? name.slice(0, MAX_NAME_LENGTH) + "\u2026"
      : name;

  const hasPrice = product.currentPrice && product.currentPrice > 0;

  return (
    // Card body opens product details. It is a role="button" container (not a
    // native <button>) so the nested "Add" button below remains valid HTML.
    <div
      role="button"
      tabIndex={0}
      onClick={onViewDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onViewDetails();
        }
      }}
      aria-label={t("components.orders.recommendationChips.viewDetailsFor", {
        name,
      })}
      className="flex-shrink-0 w-28 rounded-lg border border-primary/20 bg-card p-2 hover:border-primary/50 hover:bg-accent transition-colors text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <ProductThumbnail
        imageUrl={product.imageUrl}
        alt={product.description}
        size="lg"
        className="mb-2"
      />

      <p className="text-xs font-medium line-clamp-2 leading-tight mb-1">
        {truncatedName}
      </p>

      {hasPrice && (
        <p className="text-xs text-muted-foreground">
          ${formatCentsAsDollars(product.currentPrice!)}
        </p>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        className="w-full flex items-center justify-center gap-1 text-xs text-white font-medium mt-1.5 py-1 rounded bg-black hover:bg-black/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Plus className="h-3 w-3" />
        {t("components.orders.recommendationChips.add")}
      </button>
    </div>
  );
}

export function RecommendationChips({
  cartProductIds,
  onAddProduct,
}: RecommendationChipsProps) {
  const { t } = useTranslation();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [selectedProductForDialog, setSelectedProductForDialog] =
    useState<Product | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogQuantity, setDialogQuantity] = useState(0);
  const { capture } = useProductEvents({ component: "RecommendationChips" });
  const suggestionIdRef = useRef("");

  const { recommendedIds, isLoading } = useRecommendations(
    cartProductIds,
    true,
  );

  // Generate a stable suggestion ID each time the recommendation set changes.
  useEffect(() => {
    if (recommendedIds.length > 0) {
      suggestionIdRef.current = `suggestion_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    } else {
      suggestionIdRef.current = "";
    }
  }, [recommendedIds]);

  const cartIdSet = useMemo(() => new Set(cartProductIds), [cartProductIds]);

  const visibleIds = useMemo(
    () =>
      recommendedIds.filter(
        (id) => !cartIdSet.has(id) && !dismissedIds.has(id),
      ),
    [recommendedIds, cartIdSet, dismissedIds],
  );

  const { products } = useProductsLookup(visibleIds.slice(0, MAX_ITEMS));

  const hasProducts = products.length > 0;

  // Keep the component mounted while the details dialog is open even if the
  // recommendation list has emptied (e.g. the last suggestion was just added),
  // otherwise the dialog would unmount mid-interaction.
  if (isLoading || (!hasProducts && !selectedProductForDialog)) {
    return null;
  }

  const captureSuggestedAdd = (product: Product) => {
    const suggestionId = suggestionIdRef.current;
    if (!suggestionId) return;
    const rank = products.findIndex((p) => p.uid === product.uid);
    capture({
      event: "add_suggested_product",
      context: {
        productId: product.uid,
        rank: rank + 1,
        suggestionId,
        price: product.currentPrice ?? undefined,
      },
    });
  };

  // Quick-add from the card pill: add one and dismiss the chip.
  const handleQuickAdd = (product: Product) => {
    captureSuggestedAdd(product);
    onAddProduct(product, 1);
    setDismissedIds((prev) => new Set(prev).add(product.uid));
  };

  const handleViewDetails = (product: Product) => {
    setSelectedProductForDialog(product);
    setDialogQuantity(0);
    setDialogOpen(true);
  };

  // Add/adjust from the details dialog: the dialog stays open so the user can
  // keep tuning the quantity. Analytics fires once, on the first add (0 → >0).
  const handleDialogQuantityChange = (product: Product, quantity: number) => {
    if (dialogQuantity === 0 && quantity > 0) {
      captureSuggestedAdd(product);
    }
    setDialogQuantity(quantity);
    onAddProduct(product, quantity);
  };

  return (
    <div>
      {hasProducts && (
        <>
          <p className="text-xs text-muted-foreground mb-2">
            {t("components.orders.recommendationChips.youMightAlsoNeed")}
          </p>
          <div className="relative">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {products.slice(0, MAX_ITEMS).map((product) => (
                <RecommendationCard
                  key={product.uid}
                  product={product}
                  onAdd={() => handleQuickAdd(product)}
                  onViewDetails={() => handleViewDetails(product)}
                />
              ))}
            </div>
            {products.length > 2 && (
              <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none" />
            )}
          </div>
        </>
      )}
      {selectedProductForDialog && (
        <ProductDetailsDialog
          product={selectedProductForDialog}
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setSelectedProductForDialog(null);
              setDialogQuantity(0);
            }
          }}
          quantity={dialogQuantity}
          onQuantityChange={(quantity) =>
            handleDialogQuantityChange(selectedProductForDialog, quantity)
          }
          showCachedPricing
        />
      )}
    </div>
  );
}
